import React, { useContext, useCallback, useState } from 'react';
import { UserContext } from './AppAuthentication';
import { 
  ConsentLoadingState, 
  ConsentErrorState, 
  ConsentFormLayout, 
  ConsentCompletionState,
} from './shared/ConsentPageLayout';
import { useConsentManagement } from '../hooks/useConsentManagement';
import { LoadingState } from '../config/consentConfig';

/**
 * AuthPage Component - HRx Investigation Consent Service
 * 
 * This component provides the authenticated employee interface for the HRx Investigation
 * Consent Service. It uses the new shared components and custom hook for consistent
 * behavior and reduced code duplication.
 * 
 * Features:
 * - SAML-authenticated access via Amazon's internal federation
 * - Same UI design as Guest Page (radio buttons, modern layout)
 * - Real-time validation and submission handling
 * - Confirmation screen with submission details
 * - Shared components for consistency
 * - Enhanced error handling and retry mechanisms
 * 
 * The component integrates with AWS Cognito for user management and maintains
 * audit trails for all consent decisions.
 */

export const AuthPage: React.FC = () => {
  const { authenticatedUser, signOut } = useContext(UserContext);
  const user = authenticatedUser;
  
  // Sign out state management
  const [signOutState, setSignOutState] = useState<LoadingState>(LoadingState.IDLE);

  // Use the consent management hook
  const {
    state,
    isSubmitDisabled,
    handleConsentDecisionChange,
    handleSubmit
  } = useConsentManagement({
    userType: 'authenticated',
    userInfo: user ? {
      firstName: user.firstName,
      lastName: user.lastName,
      alias: user.alias
    } : undefined,
    requireTextInput: false // Auth page doesn't need text input validation
  });

  // Handle sign out
  const handleSignOut = useCallback(async () => {
    // Prevent multiple sign out attempts
    if (signOutState === LoadingState.LOADING) {
      return;
    }

    setSignOutState(LoadingState.LOADING);

    try {
      await signOut();
      setSignOutState(LoadingState.SUCCESS);
    } catch (error) {
      console.error('Sign out error:', error);
      setSignOutState(LoadingState.ERROR);
      // Reset state to allow retry
      setTimeout(() => setSignOutState(LoadingState.IDLE), 2000);
    }
  }, [signOut, signOutState]);

  // Show loading state while initializing
  if (state.initializationState === LoadingState.LOADING) {
    return (
      <ConsentLoadingState
        title="Initializing..."
        message="Please wait while we set up your authenticated session."
      />
    );
  }

  // Show error state if initialization failed
  if (state.initializationState === LoadingState.ERROR) {
    return (
      <ConsentErrorState
        title="Initialization Error"
        message={state.validationError || 'Failed to initialize authenticated session'}
        showRefreshHint={true}
      />
    );
  }

  // Show success/completion screen
  if (state.hasConsented) {
    return (
      <ConsentCompletionState
        consentDecision={state.consentDecision!}
        userInfo={user ? {
          firstName: user.firstName,
          lastName: user.lastName,
          alias: user.alias
        } : undefined}
        userType="authenticated"
        onSignOut={handleSignOut}
        isSigningOut={signOutState === LoadingState.LOADING}
      />
    );
  }

  // Main consent form
  return (
    <ConsentFormLayout
      consentDecision={state.consentDecision}
      onConsentDecisionChange={handleConsentDecisionChange}
      showTextInput={false}
      validationError={state.validationError}
      onSubmit={handleSubmit}
      isSubmitDisabled={isSubmitDisabled}
      isLoading={state.consentSubmissionState === LoadingState.LOADING}
    />
  );
};

export default AuthPage;
